import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  VentaPendienteMaster,
  cantidadDevueltaPendiente,
  cantidadEntregadaPendiente,
  filtrarPendientesPorFechaEmision,
  formatComprobanteVentaMasterDisplay,
  cantidadSugeridaDesdePendienteVenta,
} from '../../services/ventasSeguimientoService';
import VentaPendienteHiloInfoDialog from './VentaPendienteHiloInfoDialog';

const rowKey = (p: VentaPendienteMaster) => `${p.nroFormularioMaster}-${p.nroItemMaster}`;

const cantidadSugeridaEntregar = (p: VentaPendienteMaster): number => {
  const pendiente = Number(p.pendienteEntregar) || 0;
  return pendiente > 0 ? pendiente : cantidadSugeridaDesdePendienteVenta(p);
};

export interface BuscarPendientesEntregaDialogProps {
  open: boolean;
  loading: boolean;
  pendientes: VentaPendienteMaster[];
  onClose: () => void;
  onAgregar: (p: VentaPendienteMaster, cantidad: number) => void | Promise<void>;
  onAgregarTodos: (items: Array<{ pendiente: VentaPendienteMaster; cantidad: number }>) => void | Promise<void>;
}

const BuscarPendientesEntregaDialog: React.FC<BuscarPendientesEntregaDialogProps> = ({
  open,
  loading,
  pendientes,
  onClose,
  onAgregar,
  onAgregarTodos,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [cantidades, setCantidades] = useState<Record<string, string>>({});
  const [hiloInfo, setHiloInfo] = useState<{
    nroFormularioMaster: number;
    nroItemMaster: number;
    resumenLinea?: string;
  } | null>(null);
  const [agregandoTodos, setAgregandoTodos] = useState(false);
  const agregandoTodosRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setFechaDesde('');
    setFechaHasta('');
    setCantidades(
      pendientes.reduce<Record<string, string>>((acc, p) => {
        acc[rowKey(p)] = String(cantidadSugeridaEntregar(p));
        return acc;
      }, {}),
    );
  }, [open, pendientes]);

  const filtrados = useMemo(
    () => filtrarPendientesPorFechaEmision(pendientes, fechaDesde, fechaHasta),
    [pendientes, fechaDesde, fechaHasta],
  );

  const parseCantidad = (p: VentaPendienteMaster): number => {
    const raw = cantidades[rowKey(p)];
    if (raw === undefined) return cantidadSugeridaEntregar(p);
    const value = Number(String(raw).replace(',', '.'));
    return Number.isFinite(value) ? value : 0;
  };

  const handleAgregarTodos = async () => {
    if (agregandoTodosRef.current) return;
    if (!filtrados.length) return;

    agregandoTodosRef.current = true;
    setAgregandoTodos(true);
    await new Promise<void>((resolve) => setTimeout(resolve, 0));

    try {
      await onAgregarTodos(
        filtrados.map((p) => ({ pendiente: p, cantidad: parseCantidad(p) })),
      );
    } finally {
      agregandoTodosRef.current = false;
      setAgregandoTodos(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (agregandoTodos) return;
          onClose();
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ color: 'primary.main', fontWeight: 600 }}>Buscar pendientes</DialogTitle>
        <DialogContent>
          {loading ? (
            <Typography variant="body2" color="text.secondary">
              Cargando pendientes...
            </Typography>
          ) : pendientes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No hay líneas con cantidad pendiente de entregar para este cliente.
            </Typography>
          ) : (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, mt: 1 }}>
                <TextField
                  label="Fecha emisión desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Fecha emisión hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
              {filtrados.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No hay pendientes que coincidan con el filtro de fecha de emisión.
                </Typography>
              ) : isMobile ? (
                <Stack spacing={1.5}>
                  {filtrados.map((p) => {
                    const key = rowKey(p);
                    const cantidad = parseCantidad(p);
                    const supera = cantidad > (Number(p.pendienteEntregar) || 0);
                    return (
                      <Card key={key} variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 1.5 }}>
                          <Stack spacing={0.75}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatComprobanteVentaMasterDisplay(p)}
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                              {p.codigoArticulo} - {p.descripcionArticulo}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 700, color: 'success.dark' }}
                            >
                              Pend. entregar: {p.pendienteEntregar}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <TextField
                                label="Cant. a agregar"
                                size="small"
                                type="number"
                                value={cantidades[key] ?? ''}
                                onChange={(e) =>
                                  setCantidades((prev) => ({ ...prev, [key]: e.target.value }))
                                }
                                inputProps={{ min: 0, step: 'any' }}
                                sx={{ width: 140 }}
                                error={supera}
                              />
                              <Button
                                size="small"
                                variant="contained"
                                disabled={agregandoTodos}
                                onClick={() => void onAgregar(p, cantidad)}
                              >
                                Agregar
                              </Button>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setHiloInfo({
                                    nroFormularioMaster: p.nroFormularioMaster,
                                    nroItemMaster: p.nroItemMaster,
                                    resumenLinea: `${p.codigoArticulo || ''} ${p.descripcionArticulo || ''}`.trim(),
                                  })
                                }
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Comprobante</TableCell>
                        <TableCell>Artículo</TableCell>
                        <TableCell align="right">Pedida</TableCell>
                        <TableCell align="right">Entregada</TableCell>
                        <TableCell align="right">Devuelta</TableCell>
                        <TableCell align="right">Pend. facturar</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'success.dark' }}>
                          Pend. entregar
                        </TableCell>
                        <TableCell align="right">Cant. a agregar</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filtrados.map((p) => {
                        const key = rowKey(p);
                        const cantidad = parseCantidad(p);
                        const supera = cantidad > (Number(p.pendienteEntregar) || 0);
                        return (
                          <TableRow key={key} hover>
                            <TableCell>{formatComprobanteVentaMasterDisplay(p)}</TableCell>
                            <TableCell>
                              {p.codigoArticulo} - {p.descripcionArticulo}
                            </TableCell>
                            <TableCell align="right">{p.cantidadPedida}</TableCell>
                            <TableCell align="right">{cantidadEntregadaPendiente(p)}</TableCell>
                            <TableCell align="right">{cantidadDevueltaPendiente(p)}</TableCell>
                            <TableCell align="right">{p.pendienteFacturar}</TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: 'success.dark',
                                backgroundColor: (t) => t.palette.success.light + '14',
                              }}
                            >
                              {p.pendienteEntregar}
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={cantidades[key] ?? ''}
                                  onChange={(e) =>
                                    setCantidades((prev) => ({ ...prev, [key]: e.target.value }))
                                  }
                                  inputProps={{ min: 0, step: 'any' }}
                                  sx={{ width: 100 }}
                                  error={supera}
                                />
                                {supera && (
                                  <Tooltip title="La cantidad supera el pendiente original">
                                    <Typography variant="caption" color="warning.main">
                                      !
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={0.5} justifyContent="center">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={agregandoTodos}
                                  onClick={() => void onAgregar(p, cantidad)}
                                >
                                  Agregar
                                </Button>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setHiloInfo({
                                      nroFormularioMaster: p.nroFormularioMaster,
                                      nroItemMaster: p.nroItemMaster,
                                      resumenLinea: `${p.codigoArticulo || ''} ${p.descripcionArticulo || ''}`.trim(),
                                    })
                                  }
                                >
                                  <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={agregandoTodos}>
            Cerrar
          </Button>
          <Button
            onClick={() => void handleAgregarTodos()}
            disabled={filtrados.length === 0 || loading || agregandoTodos}
            startIcon={agregandoTodos ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {agregandoTodos ? 'Agregando…' : 'Agregar todos'}
          </Button>
        </DialogActions>
      </Dialog>

      <VentaPendienteHiloInfoDialog
        open={!!hiloInfo}
        onClose={() => setHiloInfo(null)}
        nroFormularioMaster={hiloInfo?.nroFormularioMaster ?? null}
        nroItemMaster={hiloInfo?.nroItemMaster ?? null}
        resumenLinea={hiloInfo?.resumenLinea}
      />
    </>
  );
};

export default BuscarPendientesEntregaDialog;
